import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface EvalHeaderProps {
  theme: "dark" | "light";
  onToggleTheme: () => void;
  uid: string;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}


function IconBell() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function IconHelp() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1.08 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1.08z" />
    </svg>
  );
}

function IconFeedback() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

export default function EvalHeader({ onToggleTheme, uid, onToggleSidebar }: EvalHeaderProps) {
  const { t } = useTranslation("eval");
  return (
    <header className="eval-header">
      <div className="eval-header__left">
        {/* Phones only (EvalLayout.css): the sidebar's own collapsed rail — which
            holds nothing but this button — is hidden there to give the content
            its 64px back, so the button moves up here. */}
        <button className="eval-header__menu-btn" onClick={onToggleSidebar} aria-label={t("header.openNav")}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M3 6h18M3 12h18M3 18h18" />
          </svg>
        </button>
        {/* The only way out of Eval: the Rocky sidebar links in here, but nothing
            in Eval linked back, and a phone has no URL bar habit to fall back on. */}
        <Link className="eval-header__home-link" to="/">
          <span aria-hidden="true">← </span>{t("backToRocky")}
        </Link>
      </div>

      {/* `--inert`: placeholder icons with no handler yet — hidden on phones, where
          they crowded the "Back to Rocky" link onto two lines. */}
      <div className="eval-header__right">
        <button className="eval-header__icon-btn eval-header__icon-btn--inert" title={t("header.feedback")}>
          <IconFeedback />
        </button>
        <span className="eval-header__separator" />
        <button className="eval-header__icon-btn eval-header__icon-btn--inert" title={t("header.notifications")}>
          <IconBell />
        </button>
        <span className="eval-header__separator" />
        <button className="eval-header__icon-btn eval-header__icon-btn--inert" title={t("header.help")}>
          <IconHelp />
        </button>
        <span className="eval-header__separator" />
        <button className="eval-header__icon-btn" title={t("header.settings")} onClick={onToggleTheme}>
          <IconSettings />
        </button>
        <span className="eval-header__separator" />
        <div className="eval-header__user">
          <span className="eval-header__user-name">{uid} ▾</span>
        </div>
      </div>
    </header>
  );
}
